import styled, { css } from 'styled-components'

import { device } from '~/styles/device'

export const ContainerTable = styled.section`
  ${() => css`
    &[data-freeze='first-row-and-column'] {
      --spacing-right: 0;

      @media ${device.laptop} {
        --spacing-right: 2rem;
      }

      max-height: 90dvh;
      overflow: auto;

      /* Scrollbar  */

      /* width */
      ::-webkit-scrollbar {
        height: 0.5rem;
        width: 0.5rem;
        border-radius: 0.75rem;
        position: sticky;
        bottom: 0;
        left: 0;
      }

      /* Track */
      ::-webkit-scrollbar-track {
        background: var(--sl-bg-muted);
      }

      /* Handle */
      ::-webkit-scrollbar-thumb {
        background: var(--sl-color-gray-5);
        border-radius: 0.5rem;
        /* Handle on hover */
        &:hover {
          background: var(--sl-color-gray-8);
        }
      }

      > table {
        table-layout: auto;

        tr {
          border-left: 0;
        }

        thead,
        tr > th,
        tr > td:first-child {
          position: sticky;
        }

        thead {
          top: 0;
          z-index: 50;
        }

        tbody {
          z-index: 20;
        }

        tr > th,
        tr > td {
          left: 0;
          z-index: 10;
        }

        thead tr > th:first-child {
          z-index: 35;
        }

        tbody tr > td:first-child {
          z-index: 30;
          border-left:;
        }

        tr > :first-child {
          &:after {
            content: '';
            background: rgb(222, 226, 230);
            width: 2px;
            height: 100%;
            position: absolute;
            right: 0;
            top: 0;
          }
        }
      }
    }
  `}
`

export const Thead = styled.thead`
  position: sticky;
  top: 0;
  /* Opaco (e não transparente) para o header sticky não deixar as linhas
     aparecerem por baixo durante o scroll. */
  background-color: var(--sl-bg-base-soft);
  z-index: 3;
  && > tr > th {
    background-color: var(--sl-bg-base-soft);
  }

  /* Mobile */
  @media ${device.laptop_max} {
    table.cardOnMobile & {
      display: none;
    }
  }
`

export const Tbody = styled.tbody`
  ${() => css`
    tr {
      border-left: 0.25rem solid transparent;

      td {
        transition: background-color 0.15s ease-out;

        .label-mobile {
          display: none;
        }
      }

      &:hover {
        td {
          background-color: var(--sl-bg-base-soft) !important;
        }
      }
    }

    /* Mobile */
    @media ${device.laptop_max} {
      table.cardOnMobile & {
        tr {
          position: relative;
          display: flex;
          flex-wrap: wrap;
          width: calc(100% - 1rem);
          border: 1px solid #eee;
          margin: 1.5rem 0.5rem;
          border-radius: 1rem;
          box-shadow: 0 0 3rem -0.75rem rgba(90, 90, 90, 0.25);

          > td {
            padding: 0.5rem 1rem;
            flex: 1 1 100%;

            .label-mobile {
              font-weight: 600;
              padding: 0;
            }

            &:first-child {
              border-top: none;
              padding-right: 1.25rem;
              padding-top: 0.75rem;
            }

            &:last-child {
              padding-bottom: 0.75rem;
            }

            &.col__menu,
            &.col__title {
              .label-mobile {
                display: none;
              }
            }

            &.col__menu {
              position: absolute;
              top: 0.25rem;
              right: 0.25rem;
              border-top: none;
              padding: 0;
            }

            &.col__mobile_inline {
              flex: 1 1 50%;
            }
          }
        }
      }
    }
  `}
`

export const Tr = styled.tr``

export const Th = styled.th<ThStylesProps>`
  ${({ sideBorder, activeColumn }) => css`
    transition: 0.1s ease-in-out all;

    /* Dobra a especificidade para vencer '.table > :not(caption) > * > *' */
    && {
      padding: var(--sl-space-2) var(--sl-space-3);
      font: var(--sl-text-caption-1-font);
      color: var(--sl-fg-base-soft);
      border-bottom: var(--sl-border-base);
    }

    /* Sem hover no cabeçalho: o protótipo não tem, e um '&:hover' aqui nem
       pegaria — o 'Thead' pinta o fundo por '&& > tr > th' e vence. */

    &:not(:last-child) {
      border-right: ${sideBorder ? 'var(--sl-border-base)' : 'none'};
    }

    .mainInfo {
      &:after {
        transition: all 0.3s ease-in-out;
      }
    }

    ${activeColumn &&
    css`
      .mainInfo {
        position: relative;

        .text,
        .headerSuffix {
          position: relative;
          z-index: 2;
        }

        &:after {
          content: '';
          z-index: 1;
          position: absolute;
          background-color: var(--sl-bg-base-soft);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: calc(100% + 0.75rem);
          height: calc(100% + 0.5rem);
          border-radius: 0.25rem;
          mix-blend-mode: multiply;
        }
      }
    `}
  `}
  > .contentCell {
    display: flex;
    justify-content: space-between;
  }

  /* Colunas numéricas: o título acompanha o alinhamento das células */
  &.text-end > .contentCell {
    justify-content: flex-end;
  }

  &.col {
    &__infos {
      small {
        white-space: nowrap;
      }
    }
  }
`

export const Td = styled.td<TdStylesProps>`
  ${({ sideBorder, smallerRowPadding }) => css`
    --cellPaddingY: var(${smallerRowPadding ? '--sl-space-2' : '--sl-space-3'});

    padding: var(--cellPaddingY) var(--sl-space-3) !important;

    /* Dobra a especificidade para vencer '.table > :not(caption) > * > *' */
    && {
      font: var(--sl-text-body-font);
      color: var(--sl-fg-base-soft);
      font-variant-numeric: tabular-nums;
      vertical-align: middle;
      border-bottom: var(--sl-border-base);
    }

    tr:last-child > && {
      border-bottom: none;
    }

    &:not(:last-child) {
      border-right: ${sideBorder ? 'var(--sl-border-base)' : 'none'};
    }
  `}

  &.nowrap {
    white-space: nowrap;
  }

  &.col {
    &__favicon {
      width: 3rem;

      div {
        display: flex;
        justify-content: center;
        align-items: center;

        color: var(--primary);

        &.channel {
          &__whatsapp,
          &__whatsapp_bot {
            color: #128c7e;
          }

          &__vtex,
          &__vtex_ecommerce,
          &__vtex_seasonal,
          &__magento_ecommerce {
            font-size: 18px;
            color: #3e4e6b;
          }

          &__televendas {
            color: var(--primary);
          }

          &__dia_das_maes {
            color: #f05555;
          }

          &__b2b {
            color: #f30055;
          }

          &__purchase {
            color: #54412f;
          }
        }
      }
    }

    &__infos {
      .item {
        line-height: normal;

        &__id {
          font-weight: 700;
          font-size: 1rem;
          white-space: nowrap;
        }

        &__name {
          color: var(--gray);
          line-height: 1;
          font-size: 0.75rem;
          white-space: nowrap;
        }
      }
    }

    &__payment {
      text-align: center;
      line-height: normal;

      .paymentMethod {
        padding: 0.4rem 0.75rem;
        border-radius: 2rem;
        display: flex;
        justify-content: center;
        line-height: 1;
        margin: 0.2rem 0;
        white-space: nowrap;

        border: 1px solid;

        &.online {
          color: var(--primary);
        }
        &.in_person {
          color: var(--info);
        }
        &.credit_card {
          color: var(--success);
        }
        &.no_method_chosen {
          color: var(--gray);
        }
      }
    }

    &__shipment {
      white-space: nowrap;
      line-height: 1;
    }

    &__name {
      font-size: 0.875rem;
      max-width: 10rem;
      line-height: 1.25;

      .item__name {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }

    &__actions {
      .buttons {
        display: flex;

        > * + * {
          margin-left: 0.5rem;
        }
      }
    }

    &__status {
      div {
        padding-left: 1.25rem;
        position: relative;
        line-height: 1.25;
        white-space: nowrap;

        button {
          margin-top: 0.25rem;
          white-space: nowrap;
        }

        &:before {
          content: '';
          width: 0.75rem;
          height: 0.75rem;
          border-radius: 50%;
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
        }

        &.status__ {
          &:before {
            background-color: var(--primary);
          }
          /* Blue status */
          &info {
            &:before {
              background-color: var(--info);
            }
          }
          /* Green status */
          &success,
          &finished,
          &order_delivered,
          &confirmed_order {
            &:before {
              background-color: var(--success);
            }
          }
          /* Yellow status */
          &warning,
          &pending,
          &pre_picking,
          &finished_capture,
          &waiting_capture,
          &in_delivery,
          &waiting_creation_integration,
          &waiting_picking,
          &waiting_pick_up,
          &waiting_picking_up,
          &in_picking,
          &waiting_payment,
          &waiting_delivery,
          &delivery_accepted,
          &aberto,
          &waiting_invoice,
          &invoice_issued,
          &finished_picking,
          &order_placed,
          &payment_in_analysis {
            &:before {
              background-color: var(--warning);
            }
          }
          /* Red status */
          &danger,
          &canceled,
          &order_canceled {
            &:before {
              background-color: var(--danger);
            }
          }

          /* End .status__  */
        }
      }
    }
  }
`
export const ThContentCell = styled.div`
  ${() => css`
    width: 100%;

    .sideBorder & {
      display: flex;
      justify-content: space-between;
    }

    .mainInfo {
      text-align: left;

      .text {
        font: var(--sl-text-emphasis-font);
        color: var(--sl-fg-base-soft);
      }
    }

    aside {
      display: flex;
      gap: 0.125rem;
    }
  `}
`
